import React, { useState, useEffect, useRef } from "react";
import { Table, Typography } from "antd";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import Pageloader from "../../components/loader";
import { validationErrors } from "../../utils/Helpers";
import * as workActions from "../../redux/actions/work.actions";
import { Tag } from "antd";
import WorkFilters from "./workFilters";
import moment from "moment";

const { Text, Title } = Typography;

const Work = ({ workActions, workData, workPagination, zoneData }) => {
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(10);

  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    orderCreationDate: [moment().format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")]
  });

  // Skip next useEffect run when page/size were updated by filter Search/Reset (avoids duplicate request)
  const skipNextPageSizeEffectRef = useRef(false);

  const fetchWorkList = async (extraFilters = {}, signal = null) => {
    try {
      setLoading(true);

      const finalFilters = {
        ...filters,
        ...extraFilters,
        page: extraFilters.page ?? currentPage,
        limit: extraFilters.limit ?? currentPageSize,
      };

      await workActions.getWorkFiltersPaginationAction(finalFilters, signal);
      setFilters(finalFilters);
    } catch (error) {
      if (error?.name !== "Canceled" && error?.code !== "ERR_CANCELED") validationErrors(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (value = {}) => {
    const nextPage = value.page ?? 1;
    const nextLimit = value.limit ?? currentPageSize;
    if (nextPage !== currentPage || nextLimit !== currentPageSize) {
      skipNextPageSizeEffectRef.current = true;
      setCurrentPage(nextPage);
      setCurrentPageSize(nextLimit);
    }
    fetchWorkList({ ...value, page: nextPage, limit: nextLimit });
  };

  // Build storeId → zoneName mapping
  const storeData = zoneData?.reduce((acc, item) => {
    acc[item.storeId] = item.zoneName;
    return acc;
  }, {}) || {};
  
  // Single effect: one request on mount and when user changes page/size. Cleanup aborts in-flight request so we don't get 200 + 304 on double run.
  useEffect(() => {
    if (skipNextPageSizeEffectRef.current) {
      skipNextPageSizeEffectRef.current = false;
      return;
    }
    const controller = new AbortController();
    fetchWorkList({ page: currentPage, limit: currentPageSize }, controller.signal);
    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run on page/size
  }, [currentPage, currentPageSize]);

  const statusColors = {
    open: "green",
    cancel: "red",
    completed: "blue",
    "in-process": "orange",
  };

  const workColumns = [
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (value) =>
        value ? (
          <>
            <span>{moment(value).format("DD MMMM, YYYY")}</span>
            <p>{moment(value).format("h:mm A")}</p>
          </>
        ) : (
          "-"
        ),
    },
    {
      title: "Order ID",
      dataIndex: "orderId",
      key: "orderId",
      render: (text) => text || "-",
    },
    {
      title: "Zone",
      dataIndex: "storeId",
      key: "storeId",
      render: (storeId) => storeData[storeId] || storeId || "-",
    },
    {
      title: "Customer Name",
      dataIndex: "customerName",
      key: "customerName",
      render: (text) => text || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) =>
        status ? (
          <Tag color={statusColors[status] || "default"}>
            {status.toUpperCase()}
          </Tag>
        ) : (
          "-"
        ),
    },
    // {
    //   title: "Sub Total",
    //   dataIndex: "subTotal",
    //   key: "subTotal",
    //   render: (value) =>
    //     value !== null && value !== undefined
    //       ? new Intl.NumberFormat("en-PK", {
    //           style: "currency",
    //           currency: "PKR",
    //           minimumFractionDigits: 2,
    //         }).format(value)
    //       : "-",
    // },
    {
      title: "Order Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      render: (value) =>
        value !== null && value !== undefined
          ? new Intl.NumberFormat("en-PK", {
              style: "currency",
              currency: "PKR",
              minimumFractionDigits: 0,
            }).format(value)
          : "-",
    },
    {
      title: "Invoice Amount",
      dataIndex: "invoiceAmount",
      key: "invoiceAmount",
      render: (value) =>
        value !== null && value !== undefined
          ? new Intl.NumberFormat("en-PK", {
              style: "currency",
              currency: "PKR",
              minimumFractionDigits: 0,
            }).format(value)
          : "-",
    },
    {
      title: "Work Complete Time",
      dataIndex: "workCompleteTime",
      key: "workCompleteTime",
      render: (value) =>
        value ? (
          <>
            <span>{moment(value).format("DD MMMM, YYYY")}</span>
            <p>{moment(value).format("h:mm A")}</p>
          </>
        ) : (
          "-"
        ),
    },
    {
      title: "Picture Packed Time",
      dataIndex: "packerTime",
      key: "packerTime",
      render: (value) =>
        value ? (
          <>
            <span>{moment(value).format("DD MMMM, YYYY")}</span>
            <p>{moment(value).format("h:mm A")}</p>
          </>
        ) : (
          "-"
        ),
    },
    {
      title: "Picker ID",
      key: "picker",
      render: (_, record) => {
        const pickerUser = record.user;
        return pickerUser ? (
          <>
            <Text strong>{pickerUser.displayId || "-"}</Text>{" - "}
            <Text>{pickerUser.fullName || "-"}</Text>
          </>
        ) : (
          "-"
        );
      },
    },
    {
      title: "Packer ID",
      key: "packer",
      render: (_, record) => {
        const packerUser = record.packerUser;
        return packerUser ? (
          <>
            <Text strong>{packerUser.displayId || "-"}</Text>{" - "}
            <Text>{packerUser.fullName || "-"}</Text>
          </>
        ) : (
          "-"
        );
      },
    },
  ];

  return (
    <div className="page-content">
      <div className="page-content-header" style={{ display: "flex", justifyContent: "space-between" }}>
        <Title level={2}>Works</Title>
      </div>

      {/* Filters Section */}
      <div className="p-content">
        <WorkFilters onFilterChange={handleFilterChange} />

        {loading && !(workData || []).length ? (
          <Pageloader />
        ) : (
          <Table
            className="tablehover"
            dataSource={workData || []}
            columns={workColumns}
            rowKey="_id"
            loading={loading}
            pagination={{
              total: Number(workPagination?.total) || 0,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total}`,
              current: currentPage,
              pageSize: currentPageSize,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "30", "50", "100"],
              onChange: (page, size) => {
                setCurrentPage(page);
                setCurrentPageSize(size);
              },
            }}
            onRow={(record) => ({
              onDoubleClick: () => window.open(`/works/${record.workId}`),
            })}
          />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  workData: state.WorkReducer.workData,
  workPagination: state.WorkReducer.workPagination,
  zoneData: state.ZoneReducer.zoneData,
});

const mapDispatchToProps = (dispatch) => ({
  workActions: bindActionCreators(workActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(Work);
